/**
 * MailTeamSelect: MV: the client's mail-visibility control ("Post sichtbar für").
 *
 * A native <select>: the codebase has no Radix/shadcn select and styles native
 * ones (StatusSelect, NeueAufgabeDialog, admin wizard).
 *
 * It points at ONE Kundenteam whose members may see this client's MAIL, or at
 * "Alle" (null) for no restriction. The client RECORD, the Kundenliste, the search
 * and this very detail view are NEVER restricted by it.
 *
 * Two rights meet here and they are independent: the write needs
 * `visibility.manage`, the team LIST needs `admin.team.read`. Without the write
 * right the control is read-only text. Without the list the select still renders,
 * carrying "Alle" plus the client's CURRENT team by name, so the value on screen is
 * never blanked by a right the viewer happens to lack.
 *
 * The pick is held locally until the incoming value moves off the one it replaced, because
 * the mutation persists nothing in mock mode and the value would visibly snap back. A failed
 * write reverts it and shows the error: never a silent success.
 *
 * [Source: apps/web/src/features/customers/components/StatusSelect.tsx]
 */

import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../lib/api-client';
import { cn } from '../../../lib/cn';

/** One selectable Kundenteam (id + display name). */
export interface MailTeamOption {
  id: string;
  name: string;
}

/** Shown for a mail_team_id whose team row no longer exists (dangling pointer). */
const UNKNOWN_TEAM_LABEL = 'Unbekanntes Team';
const ALL_LABEL = 'Alle';
/** Shown when the write failed for a reason the api did not put into words (e.g. no network). */
const GENERIC_WRITE_ERROR = 'Post-Sichtbarkeit konnte nicht geändert werden.';
const MAIL_TEAM_LABEL = 'Post sichtbar für';

interface MailTeamSelectProps {
  /** The client's current pointer; null = everyone may see this client's mail. */
  mailTeamId: string | null;
  /** Display name of `mailTeamId`; null when unset OR when the team row is gone. */
  mailTeamName: string | null;
  /** Rejects when the write fails; the caller does not need to handle the error. */
  onChange: (next: string | null) => Promise<unknown>;
  /** Holds `visibility.manage`: may change the pointer. */
  canManage: boolean;
  /** The tenant's teams, or undefined when the list is unavailable (no `admin.team.read`). */
  teams?: MailTeamOption[];
  className?: string;
  ariaLabel?: string;
}

export function MailTeamSelect({
  mailTeamId,
  mailTeamName,
  onChange,
  canManage,
  teams,
  className,
  ariaLabel = MAIL_TEAM_LABEL,
}: MailTeamSelectProps) {
  // The pick carries the value it replaced (`base`). Held against the base rather than
  // against itself, so an incoming value that matches NEITHER (someone else moved this
  // client meanwhile) drops the pick and wins, instead of staying hidden behind it.
  const [picked, setPicked] = useState<{ value: string | null; base: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only the LATEST write may report back. A slow first pick failing after a second pick
  // succeeded would otherwise revert the value that IS now set and cry failure over it.
  const latestWrite = useRef(0);

  useEffect(() => {
    if (picked !== null && mailTeamId !== picked.base) setPicked(null);
  }, [mailTeamId, picked]);

  const currentId = picked !== null ? picked.value : mailTeamId;
  const currentLabel =
    currentId === null
      ? ALL_LABEL
      : (teams?.find((t) => t.id === currentId)?.name ?? mailTeamName ?? UNKNOWN_TEAM_LABEL);

  if (!canManage) {
    return (
      <span
        className={cn('text-xs text-muted-foreground', className)}
        title="Keine Berechtigung, die Post-Sichtbarkeit zu ändern"
      >
        {MAIL_TEAM_LABEL}: {currentLabel}
      </span>
    );
  }

  // "Alle" plus every team we know of. A current pointer the list does not contain
  // (list unavailable, or a deleted team) is appended so the select never renders blank.
  const options: MailTeamOption[] = [...(teams ?? [])];
  if (currentId !== null && !options.some((t) => t.id === currentId)) {
    options.push({ id: currentId, name: currentLabel });
  }

  async function apply(next: string | null) {
    const write = ++latestWrite.current;
    setPicked({ value: next, base: mailTeamId });
    setError(null);
    try {
      await onChange(next);
    } catch (e) {
      if (latestWrite.current !== write) return;
      setPicked(null);
      // This route's own refusals are German and say WHY (team gone, client gone); a
      // transport failure carries technical English and is not for a broker.
      setError(e instanceof ApiError && e.message !== '' ? e.message : GENERIC_WRITE_ERROR);
    }
  }

  return (
    <span className={cn('inline-flex flex-col items-start gap-1', className)}>
      <select
        aria-label={ariaLabel}
        value={currentId ?? ''}
        // A pick must not bubble into a clickable container above the control.
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => void apply(e.target.value === '' ? null : e.target.value)}
        className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none transition-colors hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">{ALL_LABEL}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      {error !== null && (
        <span role="alert" className="text-xs text-status-danger">
          {error}
        </span>
      )}
    </span>
  );
}
